import { forwardRef, useState } from 'react';
import { Configure, Index, InstantSearch, SearchBox } from 'react-instantsearch';
// Types
import { TrackingService } from 'service/tracking/trackingService';
import type { ISearchOverlayTitles } from 'types/SearchOverlay';
import { QuerySuggestionsItem } from './components/QuerySuggestionsItem';
import { SuggestionsIndex } from './components/SuggestionsIndex';
import { NoResult } from '../SearchOverlay/NoResult';
import { HITS_PER_PAGE } from '@/config/config';

const CustomSearch = forwardRef(
  (
    {
      searchData,
      handleSubmit,
      searchValue,
      setSearchValue,
      setShow,
      searchSubmitHandler,
      autoFocus = true,
      onClick,
      queryHook,
      setHitsNumber,
    }: {
      searchData?: ISearchOverlayTitles;
      handleSubmit?: (value: string) => void;
      searchValue?: string;
      setSearchValue?: (searchValue: string) => void;
      setShow?: (isShow: boolean) => void;
      show?: boolean;
      searchSubmitHandler?: (searchValue: string) => void;
      setSearchQuery?: (searchQuery: string) => void;
      setHitsNumber?: (hitsNumber: number) => void;
      autoFocus?: boolean;
      onClick?: () => void;
      queryHook?: (query: any, search: (arg0: any) => void) => void;
    },
    parentRef: any,
  ) => {
    const [productsHits, setProductsHits] = useState<number | null>(null);
    const [recipesHits, setRecipesHits] = useState<number | null>(null);
    const [tipsTricksHits, setTipsTricksHits] = useState<number | null>(null);
    const [articlesHits, setArticlesHits] = useState<number | null>(null);

    const totalHits = [productsHits, recipesHits, tipsTricksHits, articlesHits].reduce(
      (acc, curr) => (acc && curr ? acc + curr : 0),
      0,
    );

    if (setHitsNumber) {
      setHitsNumber && setHitsNumber(totalHits ?? 0);
    }

    const showNoResults =
      totalHits === 0 &&
      productsHits === 0 &&
      recipesHits === 0 &&
      articlesHits === 0 &&
      tipsTricksHits === 0;

    const handleChange = (event: any) => {
      event.preventDefault();
      TrackingService.searchSubmit(parentRef?.current?.value, totalHits ?? 0);

      if (handleSubmit) {
        handleSubmit(event.target[0].value);
      }

      if (searchSubmitHandler) {
        searchSubmitHandler(event.target[0].value);
      }
    };

    return (
      <InstantSearch
        searchClient={searchData?.searchClient}
        indexName={searchData?.indices.recipes || ''}
        stalledSearchDelay={200}
        future={{
          preserveSharedStateOnUnmount: true,
          persistHierarchicalRootCount: true,
        }}
        insights={true}
      >
        <Configure hitsPerPage={HITS_PER_PAGE} />
        <SearchBox
          onSubmit={(event) => handleChange(event)}
          queryHook={queryHook}
          ref={parentRef}
          autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
        />

        {showNoResults && (
          <NoResult
            data={searchData}
            searchBoxData={searchValue}
            trackingEvents={{
              trackNoResultsEvent: TrackingService.trackNoResultsEvent,
              trackResetSearchBoxEvent: TrackingService.trackResetSearchBoxEvent,
            }}
          />
        )}

        {!!searchData?.indices.recipes && (
          <Index indexName={searchData?.indices.recipes}>
            <SuggestionsIndex
              searchValue={searchValue || ''}
              onClick={onClick}
              objectType="RECIPES"
              searchData={{
                copy: searchData?.copy,
              }}
              onResultsUpdate={(numberOfHits) => {
                setRecipesHits(numberOfHits);
              }}
              indexName={searchData?.indices.recipes}
              numberOfAlreadyViewedHitsBeforeThisIndex={0}
            />
          </Index>
        )}

        {!!searchData?.indices.products && (
          <Index indexName={searchData?.indices.products}>
            <SuggestionsIndex
              searchValue={searchValue || ''}
              onClick={onClick}
              objectType="PRODUCTS"
              searchData={{
                copy: searchData?.copy,
              }}
              onResultsUpdate={(numberOfHits) => {
                setProductsHits(numberOfHits);
              }}
              indexName={searchData?.indices.products}
              numberOfAlreadyViewedHitsBeforeThisIndex={recipesHits ?? 0}
            />
          </Index>
        )}

        {!!searchData?.indices.tipsAndTricks && (
          <Index indexName={searchData?.indices.tipsAndTricks}>
            <SuggestionsIndex
              searchValue={searchValue || ''}
              onClick={onClick}
              objectType="TIPS_TRICKS"
              searchData={{
                copy: searchData?.copy,
              }}
              onResultsUpdate={(numberOfHits) => {
                setTipsTricksHits(numberOfHits);
              }}
              indexName={searchData?.indices.tipsAndTricks}
              numberOfAlreadyViewedHitsBeforeThisIndex={(recipesHits ?? 0) + (productsHits ?? 0)}
            />
          </Index>
        )}

        {!!searchData?.indices.articles && (
          <Index indexName={searchData?.indices.articles}>
            <SuggestionsIndex
              searchValue={searchValue || ''}
              onClick={onClick}
              objectType="ARTICLES"
              searchData={{
                copy: searchData?.copy,
              }}
              onResultsUpdate={(numberOfHits) => {
                setArticlesHits(numberOfHits);
              }}
              indexName={searchData?.indices.articles}
              numberOfAlreadyViewedHitsBeforeThisIndex={
                (recipesHits ?? 0) + (productsHits ?? 0) + (tipsTricksHits ?? 0)
              }
            />
          </Index>
        )}

        <QuerySuggestionsItem
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          setSearchQuery={setSearchValue}
          setShow={setShow}
          searchData={searchData}
          onClick={onClick}
        />
      </InstantSearch>
    );
  },
);

export default CustomSearch;
